import {
	Box,
	Container,
	Heading,
	Text,
	SimpleGrid,
	Flex,
	Icon,
	Button,
	VStack,
	HStack,
	Center,
	Circle,
} from '@chakra-ui/react';
import { Smartphone, Star } from 'lucide-react';
import { AD, CORPORATE, SHARE_VIDEOS, USERCONTENT } from 'services/shared/imageService';
import React from 'react';
import { ROUTES } from 'services/shared/routerService';
import ScrollReveal from 'components/ScrollReveal';
import Image from 'next/image';

export default function DownloadHeroSection() {
	return (
		<Box as="main" bg="black" overflow="hidden">
			<Box as="section" pt="24" pb="20" px="4" position="relative">
				<Box position="absolute" inset="0" overflow="hidden" pointerEvents="none">
					<Circle position="absolute" top="16" left="25%" size="300px" bg="blue.500/20" filter="blur(80px)" />
					<Circle
						position="absolute"
						bottom="16"
						right="25%"
						size="300px"
						bg="teal.500/20"
						filter="blur(80px)"
					/>
				</Box>

				<Container maxW="6xl" position="relative">
					<SimpleGrid columns={{ base: 1, lg: 2 }} gap="12" alignItems="center">
						<VStack
							align={{ base: 'center', lg: 'start' }}
							textAlign={{ base: 'center', lg: 'left' }}
							gap="5"
						>
							<ScrollReveal direction="down" delay={0.1}>
								<HStack
									bg="whiteAlpha.100"
									px="3.5"
									py="1.5"
									rounded="full"
									border="1px solid"
									borderColor="whiteAlpha.200"
									w="fit-content"
								>
									<Icon as={Smartphone} size="xs" color="white" />
									<Text fontSize="xs" fontWeight="semibold" color="white">
										Available on iOS & Android
									</Text>
								</HStack>
							</ScrollReveal>

							<ScrollReveal delay={0.2}>
								<Heading
									as="h1"
									fontSize={{ base: '3xl', sm: '4xl', lg: '5xl' }}
									fontWeight="400"
									color="white"
									lineHeight="1.1"
								>
									Create Stunning Content
									<br />
									<Text
										as="span"
										bgGradient="to-r"
										gradientFrom="primary"
										gradientTo="secondary"
										bgClip="text"
									>
										From Your Pocket
									</Text>
								</Heading>
							</ScrollReveal>

							<ScrollReveal delay={0.3}>
								<Text fontSize={{ base: 'md', sm: 'lg' }} color="whiteAlpha.700" maxW="md">
									Download the Imagemotion app and create stunning content anytime, anywhere.
								</Text>
							</ScrollReveal>

							<ScrollReveal delay={0.4} direction="up">
								<Flex
									direction={{ base: 'column', sm: 'row' }}
									gap="3"
									w="full"
									justify={{ base: 'center', lg: 'start' }}
									mt="4"
								>
									<HStack
										as="a"
										// @ts-ignore
										href={ROUTES.APPLE_APP_STORE}
										target={'_blank'}
										bg="white"
										p="3.5"
										rounded="xl"
										position={'relative'}
										transition="all 0.2s"
										cursor={'default'}
										_hover={{ cursor: 'pointer', transform: 'scale(1.02)', shadow: 'xl' }}
									>
										<Box w={5} h={5} mr={2} position="relative">
											<Image
												src={CORPORATE.APP_STORE}
												alt={'app store image'}
												fill
												style={{ objectFit: 'cover' }}
											/>
										</Box>
										<Box>
											<Text fontSize="10px" color="gray.500" lineHeight="1">
												Download on the
											</Text>
											<Text fontSize="md" fontWeight="bold" color="black" lineHeight="1.2">
												App Store
											</Text>
										</Box>
									</HStack>

									<HStack
										as="a"
										// @ts-ignore
										href={ROUTES.GOOGLE_PLAY_STORE}
										target={'_blank'}
										bg="white"
										p="3.5"
										rounded="xl"
										transition="all 0.2s"
										_hover={{ cursor: 'pointer', transform: 'scale(1.02)', shadow: 'xl' }}
									>
										<Box w={5} h={5} mr={2} position="relative">
											<Image
												src={CORPORATE.PLAY_STORE}
												alt={'play store image'}
												fill
												style={{ objectFit: 'cover' }}
											/>
										</Box>
										<Box>
											<Text fontSize="10px" color="gray.500" lineHeight="1" textAlign={'left'}>
												Get it on
											</Text>
											<Text fontSize="md" fontWeight="bold" color="black" lineHeight="1.2">
												Google Play
											</Text>
										</Box>
									</HStack>
								</Flex>
							</ScrollReveal>

							<ScrollReveal delay={0.5} direction="up">
								<HStack gap="6" pt="4">
									<HStack gap="2">
										<HStack gap="0.5">
											{[...Array(5)].map((_, i) => (
												<Icon
													key={i}
													as={Star}
													size="xs"
													fill="yellow.400"
													color="yellow.400"
												/>
											))}
										</HStack>
										<Text fontSize="xs" color="whiteAlpha.600">
											4.9 Rating
										</Text>
									</HStack>
									<Text fontSize="xs" color="whiteAlpha.600">
										<Text as="span" fontWeight="bold" color="white">
											3K+
										</Text>{' '}
										Downloads
									</Text>
								</HStack>
							</ScrollReveal>
						</VStack>

						<Center position="relative">
							<ScrollReveal delay={0.3} direction="none" duration={1.5}>
								<Box
									position="absolute"
									inset="0"
									bgGradient="to-br"
									gradientFrom="blue.500/20"
									gradientTo="teal.500/20"
									filter="blur(60px)"
									transform="scale(0.8)"
								/>
							</ScrollReveal>

							<HStack gap="3" alignItems="end" position="relative">
								<ScrollReveal direction="left" delay={0.6}>
									<Box
										display={{ base: 'none', sm: 'block' }}
										w="36"
										h="320px"
										bg="gray.800"
										p="1.5"
										rounded="3xl"
										transform="rotate(-6deg) translateY(24px)"
										border="1px solid"
										borderColor="whiteAlpha.200"
									>
										<Box h="full" w="full" rounded="2xl" overflow="hidden" position="relative">
											<video
												aria-label="Clothing content"
												src={SHARE_VIDEOS.vmp4_6a489f05_0270_44e9_98b1_68df708c7c4f_hd}
												autoPlay
												playsInline
												muted
												loop
												style={{
													width: '100%',
													height: '100%',
													borderRadius: 8,
													display: 'block',
													objectFit: 'cover',
													margin: '0 auto',
												}}
											/>
											<Box
												position="absolute"
												inset="0"
												bgGradient="to-t"
												gradientFrom="blackAlpha.800"
												gradientTo="transparent"
											/>
										</Box>
									</Box>
								</ScrollReveal>

								<ScrollReveal direction="up" delay={0.4}>
									<Box
										w="52"
										h="416px"
										bg="gray.800"
										p="2"
										rounded="3xl"
										position="relative"
										zIndex="10"
										border="1px solid"
										borderColor="whiteAlpha.300"
										shadow="2xl"
									>
										<Box
											position="absolute"
											top="3"
											left="50%"
											transform="translateX(-50%)"
											w="16"
											h="4"
											bg="black"
											rounded="full"
											zIndex="20"
										/>

										<Box bg="white" h="full" rounded="3xl" overflow="hidden" p="3" pt="8">
											<Flex justify="space-between" align="center" mb="3">
												<HStack gap="1.5">
													<Flex flex="0 0 auto">
														<Image
															src={CORPORATE.LOGO_OLD}
															width={100}
															height={23}
															alt={'Logo'}
														/>
													</Flex>
												</HStack>
												<Circle size="6" bg="gray.100" />
											</Flex>

											<Box
												aspectRatio={3 / 4}
												bg="gray.200"
												rounded="xl"
												overflow="hidden"
												mb="3"
												position="relative"
											>
												<Image
													src={USERCONTENT.sixteen}
													alt="Mockup"
													fill
													style={{ objectFit: 'cover' }}
												/>
											</Box>

											<HStack gap="2">
												<Button
													size="xs"
													flex="1"
													bg="primary"
													fontWeight={800}
													color="white"
													rounded="lg"
													_hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }}
													fontSize="10px"
												>
													Generate
												</Button>
												<Button
													_hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }}
													size="xs"
													flex="1"
													bg="gray.100"
													color="black"
													rounded="lg"
													fontSize="10px"
												>
													Export
												</Button>
											</HStack>
										</Box>
									</Box>
								</ScrollReveal>

								<ScrollReveal direction="right" delay={0.6}>
									<Box
										display={{ base: 'none', sm: 'block' }}
										w="36"
										h="320px"
										bg="gray.800"
										p="1.5"
										rounded="3xl"
										transform="rotate(6deg) translateY(24px)"
										border="1px solid"
										borderColor="whiteAlpha.200"
									>
										<Box h="full" w="full" rounded="2xl" overflow="hidden" position="relative">
											<Image src={AD.FLOWER_GIRL} alt="UI" fill style={{ objectFit: 'cover' }} />
											<Box
												position="absolute"
												inset="0"
												bgGradient="to-t"
												gradientFrom="blackAlpha.800"
												gradientTo="transparent"
											/>
										</Box>
									</Box>
								</ScrollReveal>
							</HStack>
						</Center>
					</SimpleGrid>
				</Container>
			</Box>
		</Box>
	);
}
